import { useState, type FormEvent } from 'react'
import {
  CUIDARI_ACCESS_GATE_COPY,
  buildWaitlistErrorNotice,
  buildWaitlistJoinNotice,
  isLikelyAccessGateEmail,
  type AccessGateNotice,
} from '@francisquiniai/cuidari-core/auth'
import { joinWaitlist, GrowthApiError } from '@/modules/growth/api'
import { liteSession } from '@/lib/waitlist/lite-session'
import { GateBackButton, GateNoticeCard } from './gateUi'
import { WaitlistStep } from './WaitlistStatusCard'

export function WaitlistForm({
  onBack,
  onJoined,
}: {
  onBack: () => void
  onJoined: (token: string | null) => void
}) {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<AccessGateNotice | null>(null)
  const copy = CUIDARI_ACCESS_GATE_COPY.waitlist

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const normalized = email.trim().toLowerCase()
    if (!isLikelyAccessGateEmail(normalized)) {
      setNotice({
        tone: 'prompt',
        title: 'E-mail incompleto',
        body: 'Use um e-mail válido para deixar sua entrada pronta para a próxima leva.',
      })
      return
    }

    setBusy(true)
    setNotice(null)
    try {
      const res = await joinWaitlist({
        domain_slug: CUIDARI_ACCESS_GATE_COPY.productSlug,
        email: normalized,
        source: 'cuidari_web.login_gate',
        metadata: {
          locale:
            typeof navigator !== 'undefined'
              ? (navigator.language ?? 'pt-BR')
              : 'pt-BR',
        },
      })
      const token = res.entry.lite_session_token ?? null
      if (token) liteSession.setToken(token)
      onJoined(token)

      setNotice(
        buildWaitlistJoinNotice({
          status: res.entry.status,
          duplicate: res.duplicate,
        }),
      )
    } catch (err) {
      if (err instanceof GrowthApiError) {
        setNotice(buildWaitlistErrorNotice({ status: err.status, code: err.code }))
      } else if (err instanceof Error) {
        setNotice(buildWaitlistErrorNotice({ message: err.message }))
      } else {
        setNotice(buildWaitlistErrorNotice({}))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="login-card" data-testid="waitlist-form">
      <p className="login-kicker">{copy.kicker}</p>
      <h2>{copy.title}</h2>
      <p className="login-card__body">{copy.body}</p>

      <div className="login-step-list" aria-label="Como a fila anda">
        {copy.steps.map((step) => (
          <WaitlistStep
            key={step.title}
            iconName={step.icon}
            title={step.title}
            body={step.body}
            accent={step.accent}
          />
        ))}
      </div>

      {notice && <GateNoticeCard notice={notice} testId="waitlist-error" />}

      <form onSubmit={submit} className="login-form">
        <label className="login-field">
          <span>{copy.emailLabel}</span>
          <input
            type="email"
            required
            autoFocus
            placeholder={copy.emailPlaceholder}
            data-testid="waitlist-email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setNotice(null)
            }}
          />
        </label>
        <button
          type="submit"
          className="login-button login-button--primary"
          disabled={busy}
          data-testid="waitlist-submit"
        >
          {busy && <span className="login-spinner" aria-hidden="true" />}
          <span>{busy ? copy.submitBusy : copy.submitIdle}</span>
        </button>
      </form>

      <GateBackButton onClick={onBack} />
    </section>
  )
}
